import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import TenantSignupLayout from '../TenantSignupLayout';
// import { ListOfStates } from '../../components/TenantApp/ListOfStates';

type LocationState = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  parking: string;
  building: string;
  size: string;
  minStay: string;
  typeOfPlan: string;
  price: string;
  facilityID: string;
  referralCode?: string;
};

// ---- shared font styles (match SelectFacilityPage) ----
const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#222',
  fontSize: 14,
};

const labelStyle: CSSProperties = {
  ...baseFont,
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid #CCCCCC',
  fontSize: 14,
  fontFamily: baseFont.fontFamily,
  boxSizing: 'border-box' as const,
};

const errorTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#d32f2f',
  marginTop: 4,
};

const hintTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#777',
  marginTop: 4,
};

const AccountInfoPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const routeState = location.state;

  // debug: confirm we’re getting all the data
  useEffect(() => {
    console.log('[AccountInfoPage] route state:', routeState);
  }, [routeState]);

  // if user hits this page directly
  if (!routeState) {
    return (
      <TenantSignupLayout
        title="Let’s create your account"
        subtitle="We need your plan and address before we can create your login."
        step={3}
        totalSteps={7}
      >
        <div style={{ ...baseFont }}>
          <p style={{ marginBottom: 16 }}>
            It looks like you opened this page directly. Please go back to the beginning of signup.
          </p>
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={{
              ...baseFont,
              padding: '10px 16px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: '#7B1FA2',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to signup
          </button>
        </div>
      </TenantSignupLayout>
    );
  }

  // ----- form state -----
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  // const [licenseNumber, setLicenseNumber] = useState('');
  // const [licenseState, setLicenseState] = useState('CA');
  const [email, setEmail] = useState(() => {
    const prefill = sessionStorage.getItem('prefill_email') || '';
    if (prefill) sessionStorage.removeItem('prefill_email');
    return prefill;
  });
  const [password, setPassword] = useState('');
  const [passwordReentry, setPasswordReentry] = useState('');

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ----- validation (same as mobile) -----
  const isValidName = name.trim().length > 0;
  const isValidPhone = /^\d{10}$/.test(phone);

  const emailRegex = useMemo(
    () =>
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    []
  );
  const isValidEmail = emailRegex.test(email.toLowerCase());

  const passwordHasNumber = /\d/.test(password);
  const isValidPassword = password.length >= 8 && passwordHasNumber;

  const isValidPasswordReentry = passwordReentry === password && passwordReentry.length > 0;

  // const isValidLicense = licenseNumber.trim().length > 0;

  const allValid =
    isValidName &&
    isValidPhone &&
    isValidEmail &&
    isValidPassword &&
    isValidPasswordReentry
    // isValidLicense;

  const markTouched = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setTouched({
      name: true,
      phone: true,
      // licenseNumber: true,
      email: true,
      password: true,
      passwordReentry: true,
    });

    if (!allValid) return;

    try {
      setSubmitting(true);

      // 👉 match mobile signup exactly:
      // Auth.signUp({
      //   username: email,
      //   password,
      //   attributes: {
      //     name,
      //     phone_number: phone_num,
      //     'custom:specialInstructions': specialInstructions,
      //     'custom:size': size,
      //   },
      // })
      await Auth.signUp({
        username: email.trim().toLowerCase(),
        password,
        attributes: {
          name: name.trim(),
          phone_number: '+1' + phone,
          'custom:specialInstructions': routeState.specialInstructions,
          'custom:size': routeState.size,
        },
      });

      const userData = {
        facilityID: routeState.facilityID,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone: '+1' + phone,
        addressLine1: routeState.addressLine1,
        addressLine2: routeState.addressLine2,
        city: routeState.city,
        state: routeState.state,
        zip: routeState.zip,
        specialInstructions: routeState.specialInstructions,
        size: routeState.size,
        parking: routeState.parking,
        building: routeState.building,
        // licenseNumber,
        // licenseState,
        minStay: routeState.minStay,
        typeOfPlan: routeState.typeOfPlan,
        price: routeState.price,
        referralCode: routeState.referralCode,
      };

      history.push('/signup/confirm-contact-info', userData);
    } catch (err: any) {
      console.error('Error during tenant signup:', err);
      if (err?.code === 'UsernameExistsException') {
        setSubmitError(
          'That email is already in use. Please sign in instead or use a different email.'
        );
      } else {
        setSubmitError(err?.message || 'There was a problem creating your account.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TenantSignupLayout
      title="Create your account"
      subtitle="We’ll use this info to secure your storage and keep you updated about orders."
      step={3}
      totalSteps={7}
    >
      <form onSubmit={handleSubmit} style={{ ...baseFont }}>
        {/* Full name */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Full name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={() => markTouched('name')}
            placeholder="John Appleseed"
            style={inputStyle}
          />
          {touched.name && !isValidName && (
            <div style={errorTextStyle}>Please enter your full name.</div>
          )}
        </div>

        {/* Phone + license block */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelStyle}>Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '');
                setPhone(digits);
              }}
              onBlur={() => markTouched('phone')}
              placeholder="1234567890"
              style={inputStyle}
            />
            {touched.phone && !isValidPhone && (
              <div style={errorTextStyle}>Phone must be 10 digits (don’t include +1).</div>
            )}
            <div style={hintTextStyle}>We’ll use this to confirm deliveries and pickups.</div>
          </div>

          { /* <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelStyle}>Driver’s license number</label>
            <input
              type="text"
              value={licenseNumber}
              onChange={e => setLicenseNumber(e.target.value)}
              onBlur={() => markTouched('licenseNumber')}
              placeholder="DL number"
              style={inputStyle}
            />
            {touched.licenseNumber && !isValidLicense && (
              <div style={errorTextStyle}>Please enter your driver’s license number.</div>
            )}
          </div>

          <div style={{ width: 140, minWidth: 120 }}>
            <label style={labelStyle}>License state</label>
            <select
              value={licenseState}
              onChange={e => setLicenseState(e.target.value)}
              style={inputStyle}
            >
              {ListOfStates.map(s => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        */}
        </div>

        {/* Email */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value.trim())}
            onBlur={() => markTouched('email')}
            placeholder="you@example.com"
            style={inputStyle}
          />
          {touched.email && !isValidEmail && (
            <div style={errorTextStyle}>Please enter a valid email address.</div>
          )}
          <div style={hintTextStyle}>
            We’ll send a confirmation code here to verify your account.
          </div>
        </div>

        {/* Password */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onBlur={() => markTouched('password')}
            placeholder="At least 8 characters, include a number"
            style={inputStyle}
          />
          {touched.password && !isValidPassword && (
            <div style={errorTextStyle}>
              Password must be at least 8 characters and include a number.
            </div>
          )}
        </div>

        {/* Confirm password */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Confirm password</label>
          <input
            type="password"
            value={passwordReentry}
            onChange={e => setPasswordReentry(e.target.value)}
            onBlur={() => markTouched('passwordReentry')}
            placeholder="Re-enter your password"
            style={inputStyle}
          />
          {touched.passwordReentry && !isValidPasswordReentry && (
            <div style={errorTextStyle}>Passwords do not match.</div>
          )}
        </div>

        {/* Cognito / signup error */}
        {submitError && (
          <div
            style={{
              ...baseFont,
              fontSize: 13,
              color: '#d32f2f',
              marginBottom: 12,
            }}
          >
            {submitError}
          </div>
        )}

        {/* Helper / hint box (like mobile “Fill out info before you move on”) */}
        {!allValid && (
          <div
            style={{
              marginBottom: 16,
              borderRadius: 6,
              padding: 10,
              backgroundColor: 'rgba(123,31,162,0.06)',
              color: '#444',
              ...baseFont,
              fontSize: 13,
            }}
          >
            Fill out your information before you move on:
            <ul style={{ margin: '8px 0 0 18px', padding: 0, fontSize: 12 }}>
              <li>Password must be at least 8 characters and include a number.</li>
              <li>Phone number must be 10 digits (no country code).</li>
              <li>All fields above are required.</li>
            </ul>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...baseFont,
            padding: '10px 16px',
            borderRadius: 6,
            border: 'none',
            backgroundColor: '#7B1FA2',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
            textAlign: 'center' as const,
          }}
        >
          {submitting ? 'Creating account…' : 'Next'}
        </button>
      </form>
    </TenantSignupLayout>
  );
};

export default AccountInfoPage;
